import { useRef, useState } from "react";
import type { Key } from "react-aria";
import {
	Autocomplete as AriaAutocomplete,
	Menu as AriaMenu,
	MenuItem as AriaMenuItem,
	type MenuItemProps as AriaMenuItemProps,
	type MenuProps as AriaMenuProps,
	SearchField as AriaSearchField,
	Select as AriaSelect,
} from "react-aria-components";
import { useText } from "../../hooks";
import { tw } from "../../styling";
import type { SniCode } from "../../types";
import { formatSniCode } from "../../utils";
import { TextInput } from "../form-fields";
import { Overlay } from "../overlay";
import { Stack } from "../stack";
import { Text } from "../typography";
import { SelectedSniCodes } from "./selected-sni-codes";

type Props = {
	sniCodes: Array<SniCode>;
	onAdd: (sniCode: string) => void;
	onDelete: (sniCode: string) => void;
	selectedCodes: Array<string>;
};

const menuClasses = tw`border-background-divider bg-background-primary shadow-bistron max-h-64 max-w-xl overflow-x-hidden overflow-y-scroll rounded-lg border focus-visible:outline-hidden data-[empty=true]:hidden`;

const menuItemClasses = tw`text-text-primary hover:bg-background-secondary focus-visible:bg-background-secondary active:bg-background-secondary data-[focused=true]:bg-background-secondary data-[disabled=true]:text-text-disabled cursor-pointer p-4 data-[disabled=true]:cursor-default data-[disabled=true]:bg-transparent`;

const staticMenuItemClasses = tw`border-t-background-divider border-t`;

type SniRowItem = SniCode & { kind: "row" | "static" };

const SniList = ({ children, items, ...otherProps }: AriaMenuProps<SniRowItem>) => {
	const [t, texts] = useText();
	return (
		<AriaMenu
			className={menuClasses}
			items={items}
			aria-label={t(texts.kyc.sniCode.suggestions)}
			{...otherProps}
		>
			{children}
		</AriaMenu>
	);
};

const SniListRow = ({
	children,
	hasBorder,
	...otherProps
}: AriaMenuItemProps & { hasBorder: boolean }) => (
	<AriaMenuItem className={tw(menuItemClasses, hasBorder && staticMenuItemClasses)} {...otherProps}>
		{children}
	</AriaMenuItem>
);

export const SniInput = ({ sniCodes, onAdd, onDelete, selectedCodes }: Props) => {
	const [searchText, setSearchText] = useState<string>("");
	const [results, setResults] = useState<Array<SniCode>>([]);
	const [t, texts] = useText();

	const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(event.target.value);
		const searchStrings = event.target.value.replace(".", "").toLowerCase().split(" ");
		setResults(
			sniCodes.filter((s) =>
				searchStrings.every((subString) =>
					`${s.sniCode} ${s.description.toLowerCase()}`.includes(subString),
				),
			),
		);
	};

	const inputRef = useRef<HTMLInputElement>(null);

	const onAction = (key: Key) => {
		onAdd(key.toString());
		setSearchText("");
		setTimeout(() => {
			inputRef.current?.blur();
			inputRef.current?.focus();
		}, 0);
	};

	const onOverlayClose = () => {
		setSearchText("");
		setTimeout(() => {
			inputRef.current?.blur();
		}, 0);
	};

	const getMenuItems = (results: Array<SniCode>): Array<SniRowItem> => [
		...results.slice(0, 20).map(
			({ description, sniCode }) =>
				({
					description,
					sniCode,
					kind: "row",
				}) satisfies SniRowItem,
		),
		...(results.length > 20
			? [
					{
						kind: "static",
						sniCode: "static",
						description: t(texts.kyc.sniCode.tooManyResults),
					} satisfies SniRowItem,
				]
			: []),
		...(results.length < 1
			? [
					{
						kind: "static",
						sniCode: "static",
						description: t(texts.kyc.sniCode.noResults),
					} satisfies SniRowItem,
				]
			: []),
	];

	return (
		<Stack gap={4}>
			<AriaSelect aria-label={t(texts.kyc.sniCode.searchSniLabel)}>
				<AriaAutocomplete inputValue={searchText} onInputChange={setSearchText}>
					<AriaSearchField autoFocus aria-label={t(texts.kyc.sniCode.searchSniLabel)}>
						<TextInput
							label={t(texts.kyc.sniCode.searchSniLabel)}
							onChange={onSearchChange}
							value={searchText}
							ref={inputRef}
						/>
					</AriaSearchField>
					<Overlay targetRef={inputRef} isOpen={searchText.length > 1} onClose={onOverlayClose}>
						<SniList
							dependencies={[searchText, selectedCodes]}
							disabledKeys={[...selectedCodes, "static"]}
							items={searchText.length > 1 ? getMenuItems(results) : []}
							onAction={onAction}
						>
							{(item) => (
								<SniListRow
									id={item.sniCode}
									key={item.sniCode}
									textValue={item.description}
									aria-label={item.description}
									hasBorder={item.kind === "static"}
								>
									{item.kind === "row" ? (
										<Text
											className={
												selectedCodes.includes(item.sniCode)
													? "text-text-disabled"
													: "text-text-primary"
											}
										>
											<Text
												bold
												className={
													selectedCodes.includes(item.sniCode)
														? "text-text-disabled"
														: "text-text-primary"
												}
											>{`${formatSniCode(item.sniCode)} `}</Text>
											{`${item.description}`}
										</Text>
									) : (
										<Text className="text-text-disabled">{item.description}</Text>
									)}
								</SniListRow>
							)}
						</SniList>
					</Overlay>
				</AriaAutocomplete>
			</AriaSelect>
			<SelectedSniCodes
				allCodes={sniCodes}
				selectedCodes={selectedCodes}
				onDeleteClick={onDelete}
			/>
		</Stack>
	);
};
